import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getMySalon } from "@/lib/salon.functions";
import {
  confirmCheckout,
  createCoinCheckoutSession,
  openBillingPortal,
} from "@/lib/payments.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { COIN_PRICE_CENTS, DAILY_BUDGET_EUR, formatEuro, grossFromNet } from "@/lib/salonturbo";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/dashboard/coins")({
  component: CoinsPage,
});

function CoinsPage() {
  const queryClient = useQueryClient();
  const fetchSalon = useServerFn(getMySalon);
  const checkout = useServerFn(createCoinCheckoutSession);
  const portal = useServerFn(openBillingPortal);
  const confirm = useServerFn(confirmCheckout);
  const { data, isLoading } = useQuery({ queryKey: ["my-salon"], queryFn: () => fetchSalon() });
  const [coins, setCoins] = useState(4);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", "/dashboard/coins");
      toast.info("Zahlung abgebrochen.");
      return;
    }
    if (params.get("checkout") !== "success" || !sessionId) return;
    void confirm({ data: { sessionId } })
      .then(() => toast.success("Coins wurden gutgeschrieben."))
      .catch(() => toast.info("Zahlung eingegangen – Guthaben wird verarbeitet."))
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: ["my-salon"] });
        window.history.replaceState({}, "", "/dashboard/coins");
      });
  }, [confirm, queryClient]);

  const buy = useMutation({
    mutationFn: () =>
      checkout({ data: { salonId: data!.salon!.id, coins, returnPath: "/dashboard/coins" } }),
    onSuccess: (result) => {
      if (result.url) window.location.href = result.url;
    },
    onError: () => toast.error("Zahlung konnte nicht gestartet werden."),
  });

  const billing = useMutation({
    mutationFn: () => portal({ data: { salonId: data!.salon!.id } }),
    onSuccess: (result) => {
      if (result.url) window.location.href = result.url;
    },
    onError: () => toast.error("Zahlungsportal noch nicht verfügbar."),
  });

  if (isLoading || !data?.salon) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Coins aufladen</CardTitle>
          <CardDescription>
            1 Coin = 1 Boost-Tag mit {DAILY_BUDGET_EUR} € Google-Budget ={" "}
            {formatEuro(COIN_PRICE_CENTS)} netto zzgl. MwSt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Aktuelles Guthaben: <strong className="text-foreground">{data.coins} Coins</strong>
          </p>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setCoins((v) => Math.max(1, v - 1))}>
              –
            </Button>
            <span className="font-display text-3xl font-semibold">{coins}</span>
            <Button variant="outline" size="icon" onClick={() => setCoins((v) => Math.min(100, v + 1))}>
              +
            </Button>
            <span className="text-sm text-muted-foreground">
              = {formatEuro(coins * COIN_PRICE_CENTS)} netto
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatEuro(grossFromNet(coins * COIN_PRICE_CENTS))} inkl. 19 % MwSt. – der endgültige
            Steuersatz wird im Checkout anhand deiner Rechnungsadresse ermittelt (USt-IdNr. für
            Reverse Charge kann dort angegeben werden).
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Button
              className="w-full sm:w-auto"
              onClick={() => buy.mutate()}
              disabled={buy.isPending}
            >
              {buy.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Jetzt kaufen
            </Button>
            <Button
              variant="outline"
              className="w-full whitespace-normal text-center sm:w-auto"
              onClick={() => billing.mutate()}
              disabled={billing.isPending}
            >
              Zahlungsdaten & Rechnungen verwalten
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Mit dem Kauf akzeptierst du die{" "}
            <Link to="/agb" className="underline hover:text-foreground">
              AGB
            </Link>
            . Coins verfallen nicht, eine Auszahlung in Geld ist ausgeschlossen.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">Rechnungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.payments.length === 0 && (
            <p className="text-sm text-muted-foreground">Noch keine Zahlungen vorhanden.</p>
          )}
          {data.payments.map((payment) => (
            <div key={payment.id} className="flex items-center justify-between gap-3 border-b pb-3 last:border-0">
              <div>
                <p className="text-sm font-medium">
                  {payment.coins} Coins ·{" "}
                  {payment.status === "paid"
                    ? `${formatEuro(payment.amount_cents)} inkl. MwSt.`
                    : `${formatEuro(payment.amount_cents)} netto`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(payment.created_at).toLocaleDateString("de-DE")} ·{" "}
                  {payment.status === "paid" ? "bezahlt" : payment.status}
                </p>
              </div>
              {payment.invoice_url && (
                <Button asChild variant="ghost" size="sm">
                  <a href={payment.invoice_url} target="_blank" rel="noreferrer">
                    Rechnung
                  </a>
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
